// Grid-based clustering for web map zoom levels.
// At high zoom (>=16) every POI is shown individually.
// At lower zooms, nearby POIs are grouped into a single cluster item.

const CLUSTER_RADIUS_PX = 60; // screen pixels before clustering

// Converts lat/lng to pixel coordinates at a given zoom level (Web Mercator).
function latLngToPixel(lat, lng, zoom) {
  const scale = 256 * Math.pow(2, zoom);
  const x = ((lng + 180) / 360) * scale;
  const sinLat = Math.sin((lat * Math.PI) / 180);
  const y = ((0.5 - Math.log((1 + sinLat) / (1 - sinLat)) / (4 * Math.PI)) * scale);
  return { x, y };
}

export function clusterPOIs(pois, zoom) {
  if (!pois || pois.length === 0) return [];

  // At high zoom show everything individually
  if (zoom >= 16) return pois.map((p) => ({ ...p, isCluster: false }));

  const threshold = CLUSTER_RADIUS_PX * Math.pow(2, 16 - zoom);
  const used = new Set();
  const result = [];

  for (let i = 0; i < pois.length; i++) {
    if (used.has(i)) continue;

    const a = pois[i];
    const pa = latLngToPixel(parseFloat(a.lat), parseFloat(a.lng), zoom);
    const members = [a];
    used.add(i);

    for (let j = i + 1; j < pois.length; j++) {
      if (used.has(j)) continue;
      const b = pois[j];
      const pb = latLngToPixel(parseFloat(b.lat), parseFloat(b.lng), zoom);
      const dx = pa.x - pb.x;
      const dy = pa.y - pb.y;
      if (Math.sqrt(dx * dx + dy * dy) < threshold) {
        members.push(b);
        used.add(j);
      }
    }

    if (members.length === 1) {
      result.push({ ...a, isCluster: false });
    } else {
      // Centroid
      const avgLat = members.reduce((s, p) => s + parseFloat(p.lat), 0) / members.length;
      const avgLng = members.reduce((s, p) => s + parseFloat(p.lng), 0) / members.length;
      result.push({
        isCluster: true,
        id: `cluster-${i}`,
        lat: avgLat,
        lng: avgLng,
        count: members.length,
        pois: members,
        // Use zone color of first member for the cluster badge
        zone: members[0].zone,
      });
    }
  }

  return result;
}
